import React, { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { useAdminTheme } from '@/contexts/AdminThemeContext';
import { Shield, LogIn, UserPlus, Loader2, AlertCircle, Smartphone, Copy, Check, KeyRound } from 'lucide-react';
import PasswordInput from '@/components/PasswordInput';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { BACKEND_URL } from '@/utils/apiConfig';

export default function AdminLoginPage() {
  const { admin, login, register, verify2FA } = useAdmin();
  const { isDark, colors } = useAdminTheme();
  const [step, setStep] = useState('auth');
  const [mode, setMode] = useState('login');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [adminId, setAdminId] = useState('');
  const [setupToken, setSetupToken] = useState('');
  const [qrCode, setQrCode] = useState('');
  const [totpSecret, setTotpSecret] = useState('');
  const [totpCode, setTotpCode] = useState('');
  const [secretCopied, setSecretCopied] = useState(false);

  // Forgot-password state
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [forgotInfo, setForgotInfo] = useState('');

  // Redirect to admin dashboard when authenticated
  useEffect(() => {
    if (admin) {
      window.location.replace('/admin');
    }
  }, [admin]);

  const handleAuth = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'register') {
        const res = await register(email, password, name);
        setAdminId(res.admin_id);
        setSetupToken(res.setup_token);
        setQrCode(res.qr_code);
        setTotpSecret(res.totp_secret);
        setStep('2fa-setup');
      } else {
        const res = await login(email, password);
        if (res.requires_2fa_setup) {
          setAdminId(res.admin_id);
          setSetupToken(res.setup_token);
          setQrCode(res.qr_code);
          setTotpSecret(res.totp_secret);
          setStep('2fa-setup');
        } else if (res.requires_2fa) {
          setAdminId(res.admin_id);
          setStep('2fa-verify');
        }
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (step === '2fa-setup') {
        await verify2FA(adminId, totpCode, setupToken);
      } else {
        await login(email, password, totpCode);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || 'Invalid verification code');
    } finally {
      setLoading(false);
    }
  };

  const copySecret = async () => {
    await navigator.clipboard.writeText(totpSecret);
    setSecretCopied(true);
    setTimeout(() => setSecretCopied(false), 2000);
  };

  const handleReset2FA = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/auth/reset-2fa`, {
        email,
        password,
      });
      setAdminId(res.data.admin_id);
      setSetupToken(res.data.setup_token);
      setQrCode(res.data.qr_code);
      setTotpSecret(res.data.totp_secret);
      setTotpCode('');
      setStep('2fa-setup');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed. Check your email and password.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setForgotInfo('');
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/auth/forgot-password`, { email });
      setForgotInfo(
        "If an admin account exists with that email, a 6-digit reset code has been sent. Check your inbox (and spam folder)."
      );
      setStep('forgot-verify');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Could not send reset code. Try again in a moment.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/admin/auth/reset-password`, {
        email,
        code: resetCode,
        new_password: newPassword,
      });
      setForgotInfo('Password reset successfully. Sign in with your new password and your authenticator code.');
      setPassword('');
      setResetCode('');
      setNewPassword('');
      setStep('auth');
    } catch (err) {
      setError(err?.response?.data?.detail || 'Reset failed. Check the code and try again.');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = { background: colors.input, border: `1px solid ${colors.inputBorder}`, color: colors.text };
  const cardStyle = { background: colors.card, border: `1px solid ${colors.cardBorder}` };

  return (
    <div className="min-h-dvh flex items-center justify-center px-4" style={{ background: colors.bg }} data-testid="admin-login-page">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-indigo-500/10 mb-4">
            <Shield className="h-7 w-7 text-indigo-400" />
          </div>
          <h1 className="text-2xl font-bold" style={{ color: colors.text }}>Eili Admin</h1>
          <p className="text-sm mt-1" style={{ color: colors.textSecondary }}>
            {step === 'auth' && (mode === 'register' ? 'Create your admin account' : 'Sign in to the dashboard')}
            {step === '2fa-setup' && 'Set up two-factor authentication'}
            {step === '2fa-verify' && 'Enter your authenticator code'}
            {step === '2fa-reset' && 'Reset your two-factor authentication'}
            {step === 'forgot-request' && 'Forgot your password'}
            {step === 'forgot-verify' && 'Reset your password'}
          </p>
        </div>

        {/* Step: Auth (email/password) */}
        {step === 'auth' && (
          <form onSubmit={handleAuth} className="space-y-4">
            <div className="rounded-2xl p-6 space-y-4" style={cardStyle}>
              {mode === 'register' && (
                <div>
                  <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Full Name</label>
                  <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full rounded-xl px-4 py-3 text-sm placeholder-slate-500 focus:outline-none transition" style={inputStyle} placeholder="Your name" required data-testid="admin-name-input" />
                </div>
              )}
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl px-4 py-3 text-sm placeholder-slate-500 focus:outline-none transition" style={inputStyle} placeholder={mode === 'register' ? 'admin@askeili.com' : 'Your admin email'} required data-testid="admin-email-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Password</label>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl px-4 py-3 text-sm placeholder-slate-500 focus:outline-none transition" style={inputStyle} placeholder="********" required data-testid="admin-password-input" />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2" data-testid="admin-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full rounded-xl py-5 font-medium gap-2 text-white" style={{ background: colors.accent }} data-testid="admin-submit-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : mode === 'register' ? <UserPlus className="h-4 w-4" /> : <LogIn className="h-4 w-4" />}
                {mode === 'register' ? 'Create Account' : 'Sign In'}
              </Button>
            </div>

            <p className="text-center text-xs" style={{ color: colors.textMuted }}>
              {mode === 'login' ? (
                <>First time? <button type="button" onClick={() => { setMode('register'); setError(''); }} className="text-indigo-400 hover:underline" data-testid="admin-switch-register">Register here</button></>
              ) : (
                <>Already registered? <button type="button" onClick={() => { setMode('login'); setError(''); }} className="text-indigo-400 hover:underline" data-testid="admin-switch-login">Sign in</button></>
              )}
            </p>

            {mode === 'login' && (
              <p className="text-center text-xs">
                <button
                  type="button"
                  onClick={() => { setStep('forgot-request'); setError(''); setForgotInfo(''); }}
                  className="text-indigo-400 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </button>
              </p>
            )}

            {forgotInfo && step === 'auth' && (
              <p className="text-center text-xs text-emerald-400/90" data-testid="admin-forgot-info">{forgotInfo}</p>
            )}

            {mode === 'register' && (
              <p className="text-center text-[11px]" style={{ color: colors.textMuted }}>Only admin@askeili.com can self-register.</p>
            )}
          </form>
        )}

        {/* Step: 2FA Setup (QR code + verify) */}
        {step === '2fa-setup' && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="rounded-2xl p-6 space-y-5" style={cardStyle}>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-500/15 mb-3">
                  <Smartphone className="h-5 w-5 text-indigo-400" />
                </div>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  Scan this QR code with your authenticator app
                </p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>
                  Google Authenticator, Microsoft Authenticator, or Authy
                </p>
              </div>

              {qrCode && (
                <div className="flex justify-center">
                  <div className="bg-white p-3 rounded-xl" data-testid="totp-qr-code">
                    <img src={`data:image/png;base64,${qrCode}`} alt="2FA QR Code" className="w-48 h-48" />
                  </div>
                </div>
              )}

              <div>
                <p className="text-[11px] text-center mb-1.5" style={{ color: colors.textMuted }}>Or enter this code manually:</p>
                <div className="flex items-center justify-center gap-2 rounded-xl px-3 py-2" style={{ background: colors.input }}>
                  <code className="text-xs text-indigo-300 font-mono tracking-widest" data-testid="totp-secret">{totpSecret}</code>
                  <button type="button" onClick={copySecret} className="p-1 transition" style={{ color: colors.textMuted }}>
                    {secretCopied ? <Check className="h-3.5 w-3.5 text-green-400" /> : <Copy className="h-3.5 w-3.5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Verification Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full rounded-xl px-4 py-3 text-center text-lg font-mono tracking-[0.3em] placeholder-slate-500 focus:outline-none transition"
                  style={inputStyle}
                  placeholder="000000"
                  autoFocus
                  required
                  data-testid="totp-code-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2" data-testid="admin-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}

              <Button type="submit" disabled={loading || totpCode.length !== 6} className="w-full rounded-xl py-5 font-medium gap-2 text-white" style={{ background: colors.accent }} data-testid="totp-verify-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Shield className="h-4 w-4" />}
                Verify & Continue
              </Button>
            </div>
          </form>
        )}

        {/* Step: 2FA Verify (login with code) */}
        {step === '2fa-verify' && (
          <form onSubmit={handleVerify} className="space-y-4">
            <div className="rounded-2xl p-6 space-y-5" style={cardStyle}>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-500/15 mb-3">
                  <Shield className="h-5 w-5 text-indigo-400" />
                </div>
                <p className="text-sm" style={{ color: colors.textSecondary }}>
                  Enter the code from your authenticator app
                </p>
              </div>

              <div>
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={totpCode}
                  onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ''))}
                  className="w-full rounded-xl px-4 py-4 text-center text-2xl font-mono tracking-[0.4em] placeholder-slate-500 focus:outline-none transition"
                  style={inputStyle}
                  placeholder="000000"
                  autoFocus
                  required
                  data-testid="totp-code-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2" data-testid="admin-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}

              <Button type="submit" disabled={loading || totpCode.length !== 6} className="w-full rounded-xl py-5 font-medium gap-2 text-white" style={{ background: colors.accent }} data-testid="totp-verify-btn">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
                Verify & Sign In
              </Button>

              <button
                type="button"
                onClick={() => { setStep('auth'); setTotpCode(''); setError(''); }}
                className="w-full text-xs transition"
                style={{ color: colors.textMuted }}
              >
                Use a different account
              </button>

              <button
                type="button"
                onClick={() => { setStep('2fa-reset'); setTotpCode(''); setError(''); }}
                className="w-full text-xs text-amber-400/70 hover:text-amber-300 transition"
                data-testid="reset-2fa-link"
              >
                Can't access your authenticator? Reset 2FA
              </button>
            </div>
          </form>
        )}

        {/* Step: 2FA Reset */}
        {step === '2fa-reset' && (
          <form onSubmit={handleReset2FA} className="space-y-4">
            <div className="rounded-2xl p-6 space-y-4" style={cardStyle}>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/15 mb-3">
                  <KeyRound className="h-5 w-5 text-amber-400" />
                </div>
                <p className="text-sm" style={{ color: colors.textSecondary }}>Confirm your identity to reset 2FA</p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Enter your email and password to generate a new QR code</p>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full rounded-xl px-4 py-3 text-sm placeholder-slate-500 focus:outline-none transition" style={inputStyle} placeholder="Your admin email" required data-testid="reset-email-input" />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Password</label>
                <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} className="w-full rounded-xl px-4 py-3 text-sm placeholder-slate-500 focus:outline-none transition" style={inputStyle} placeholder="********" required data-testid="reset-password-input" />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2" data-testid="admin-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}

              <Button type="submit" disabled={loading} className="w-full rounded-xl py-5 font-medium gap-2 bg-amber-600 hover:bg-amber-500 text-white" data-testid="reset-2fa-submit">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Reset & Set Up New 2FA
              </Button>

              <button
                type="button"
                onClick={() => { setStep('auth'); setError(''); }}
                className="w-full text-xs transition"
                style={{ color: colors.textMuted }}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* Step: Forgot password — request code */}
        {step === 'forgot-request' && (
          <form onSubmit={handleForgotPassword} className="space-y-4">
            <div className="rounded-2xl p-6 space-y-4" style={cardStyle}>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-500/15 mb-3">
                  <KeyRound className="h-5 w-5 text-indigo-400" />
                </div>
                <p className="text-sm" style={{ color: colors.textSecondary }}>Reset your admin password</p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>We&apos;ll email a 6-digit code to your admin email.</p>
              </div>

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Admin email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-sm placeholder-slate-500 focus:outline-none transition"
                  style={inputStyle}
                  placeholder="admin@askeili.com"
                  required
                  data-testid="forgot-email-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2" data-testid="admin-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl py-5 font-medium gap-2 text-white"
                style={{ background: colors.accent }}
                data-testid="forgot-send-code-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Send reset code
              </Button>

              <p className="text-center text-xs" style={{ color: colors.textMuted }}>
                Already have a code?{' '}
                <button
                  type="button"
                  onClick={() => { setStep('forgot-verify'); setError(''); }}
                  className="text-indigo-400 hover:underline"
                  data-testid="forgot-have-code-link"
                >
                  Enter it here
                </button>
              </p>

              <button
                type="button"
                onClick={() => { setStep('auth'); setError(''); }}
                className="w-full text-xs transition"
                style={{ color: colors.textMuted }}
                data-testid="forgot-back-link"
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}

        {/* Step: Forgot password — enter code + new password */}
        {step === 'forgot-verify' && (
          <form onSubmit={handleResetPassword} className="space-y-4">
            <div className="rounded-2xl p-6 space-y-4" style={cardStyle}>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-indigo-500/15 mb-3">
                  <KeyRound className="h-5 w-5 text-indigo-400" />
                </div>
                <p className="text-sm" style={{ color: colors.textSecondary }}>Enter the 6-digit code</p>
                <p className="text-xs mt-1" style={{ color: colors.textMuted }}>Check your email for the code (expires in 15 min).</p>
              </div>

              {forgotInfo && (
                <p className="text-xs text-emerald-400/90 text-center" data-testid="forgot-info">{forgotInfo}</p>
              )}

              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>Admin email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-sm placeholder-slate-500 focus:outline-none transition"
                  style={inputStyle}
                  required
                  data-testid="forgot-verify-email-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>6-digit code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  value={resetCode}
                  onChange={(e) => setResetCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  className="w-full rounded-xl px-4 py-3 text-sm tracking-widest text-center focus:outline-none transition"
                  style={inputStyle}
                  placeholder="123456"
                  required
                  maxLength={6}
                  data-testid="forgot-code-input"
                />
              </div>
              <div>
                <label className="block text-xs font-medium mb-1.5" style={{ color: colors.textSecondary }}>New password</label>
                <PasswordInput
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full rounded-xl px-4 py-3 text-sm focus:outline-none transition"
                  style={inputStyle}
                  placeholder="Min 8 chars, 1 uppercase, 1 number"
                  required
                  data-testid="forgot-new-password-input"
                />
              </div>

              {error && (
                <div className="flex items-center gap-2 text-sm text-red-400 bg-red-400/10 rounded-xl px-3 py-2" data-testid="admin-error">
                  <AlertCircle className="h-4 w-4 shrink-0" /> {error}
                </div>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-xl py-5 font-medium gap-2 text-white"
                style={{ background: colors.accent }}
                data-testid="forgot-submit-btn"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />}
                Reset password
              </Button>

              <button
                type="button"
                onClick={() => { setStep('forgot-request'); setError(''); }}
                className="w-full text-xs transition"
                style={{ color: colors.textMuted }}
                data-testid="forgot-resend-link"
              >
                Didn&apos;t receive the code? Resend
              </button>
              <button
                type="button"
                onClick={() => { setStep('auth'); setError(''); }}
                className="w-full text-xs transition"
                style={{ color: colors.textMuted }}
              >
                Back to sign in
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
